import api from 'config/api.config';
import { getAccessToken } from 'utils/auth/auth';
import { AxiosResponse } from 'axios';

// Define interfaces for request payloads
interface LoginPayload {
  email: string;
  password: string;
}

interface GoogleLoginPayload {
  idToken: string;
  preferred_language?: string;
}

interface RegisterPayload {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  preferred_language?: string;
}

interface RefreshTokenPayload {
  refreshToken: string;
}

interface UpdateNamePayload {
  first_name: string;
  last_name: string;
}

interface UpdatePasswordPayload {
  current_password: string;
  new_password: string;
}

interface UploadPicturePayload {
  base64Image: string;
}

interface UpdatePhonePayload {
  phone_number: string;
}

interface VerifyPhonePayload {
  otp: string;
}

interface UpdateLanguagePayload {
  language: string;
}

interface VerifyOTPPayload {
  userId: string;
  otp: string;
}

interface RequestPasswordResetPayload {
  email: string;
}

interface ResetPasswordPayload {
  email: string;
  otp: string;
  new_password: string;
}

// Define interface for login response
interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    email: string;
    fullName: string;
    role: string;
    _id: string;
  };
  message?: string;
}

// Define interface for registration response
interface RegisterResponse {
  message: string;
  userId: string;
}

// Define interface for refresh token response
interface RefreshTokenResponse {
  accessToken: string;
}

// Define generic interface for other responses
interface GenericAuthResponse<T = any> {
  data?: T;
  message?: string;
}

// Define interface for validate-auth response
interface ValidateAuthResponse {
  expiryDate: string;
  isExpired: boolean;
  daysRemaining: number;
  planName: string;
}

interface Profile {
  id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  language?: string;
  preferred_language?: string;
  twoFactorEnabled?: boolean;
  profile_picture?: string; // Explicitly typed as string | undefined
  [key: string]: unknown; // Kept for flexibility, but can be removed if stricter typing is desired
}

const LOGIN_API_URL = '/api/auth/login';
const GOOGLE_LOGIN_API_URL = '/api/auth/google-login';
const REGISTER_API_URL = '/api/auth/register';
const REFRESH_TOKEN_URL = '/api/auth/refresh-token';
const VALIDATE_TOKEN_URL = '/api/auth/validate-token';
const VALIDATE_AUTH_URL = '/api/auth/validate-auth';
const VERIFY_EMAIL_URL = '/api/auth/verify-email';
const VERIFY_OTP_URL = '/api/auth/verify-otp';
const REQUEST_PASSWORD_RESET_URL = '/api/auth/request-password-reset';
const RESET_PASSWORD_URL = '/api/auth/reset-password';
const REQUEST_EMAIL_OTP_URL = '/api/auth/request-email-otp';

const PROFILE_BASE_URL = '/api/auth/profile';
const PROFILE_DETAILS_URL = PROFILE_BASE_URL;
const UPDATE_NAME_URL = `${PROFILE_BASE_URL}/name`;
const UPDATE_PASSWORD_URL = `${PROFILE_BASE_URL}/password`;
const UPLOAD_PICTURE_URL = `${PROFILE_BASE_URL}/picture`;
const UPDATE_PHONE_URL = `${PROFILE_BASE_URL}/phone`;
const VERIFY_PHONE_URL = `${PROFILE_BASE_URL}/verify-phone`;
const UPDATE_LANGUAGE_URL = `${PROFILE_BASE_URL}/language`;
const ENABLE_2FA_URL = `${PROFILE_BASE_URL}/enable-2fa`;
const DISABLE_2FA_URL = `${PROFILE_BASE_URL}/disable-2fa`;
const DELETE_ACCOUNT_URL = PROFILE_BASE_URL;

const getAuthHeader = (): { headers: { Authorization: string } } => {
  const token: string | null = getAccessToken();
  if (!token) {
    throw new Error('No access token found');
  }
  return {
    headers: { Authorization: `Bearer ${token}` },
  };
};

export const authService = {
  requestEmailOTP: async (): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.post<GenericAuthResponse>(REQUEST_EMAIL_OTP_URL, {}, getAuthHeader());
  },

  login: async (email: string, password: string): Promise<AxiosResponse<AuthResponse>> => {
    const response = await api.post<AuthResponse>(LOGIN_API_URL, { email, password } as LoginPayload);
    return response;
  },

  googleLogin: async (idToken: string, preferred_language?: string): Promise<AxiosResponse<AuthResponse>> => {
    try {
      const response = await api.post<AuthResponse>(GOOGLE_LOGIN_API_URL, { idToken, preferred_language } as GoogleLoginPayload);
      return response;
    } catch (error: unknown) {
      throw error;
    }
  },

  register: async (
    email: string,
    password: string,
    first_name: string,
    last_name: string,
    preferred_language?: string
  ): Promise<AxiosResponse<RegisterResponse>> => {
    return api.post<RegisterResponse>(REGISTER_API_URL, {
      email,
      password,
      first_name,
      last_name,
      preferred_language,
    } as RegisterPayload);
  },

  verifyOTP: async (userId: string, otp: string): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.post<GenericAuthResponse>(VERIFY_OTP_URL, { userId, otp } as VerifyOTPPayload);
  },

  requestPasswordReset: async (email: string): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.post<GenericAuthResponse>(REQUEST_PASSWORD_RESET_URL, { email } as RequestPasswordResetPayload);
  },

  resetPassword: async (
    email: string,
    otp: string,
    new_password: string
  ): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.post<GenericAuthResponse>(RESET_PASSWORD_URL, {
      email,
      otp,
      new_password,
    } as ResetPasswordPayload);
  },

  refreshToken: async (refreshToken: string): Promise<AxiosResponse<RefreshTokenResponse>> => {
    return api.post<RefreshTokenResponse>(REFRESH_TOKEN_URL, { refreshToken } as RefreshTokenPayload);
  },

  validateToken: async (): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.get<GenericAuthResponse>(VALIDATE_TOKEN_URL, getAuthHeader());
  },

  validateAuth: async (): Promise<AxiosResponse<ValidateAuthResponse>> => {
    return api.get<ValidateAuthResponse>(VALIDATE_AUTH_URL, getAuthHeader());
  },

  verifyEmail: async (token: string): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.get<GenericAuthResponse>(`${VERIFY_EMAIL_URL}/${token}`);
  },
};

export const profileService = {
  getProfile: async (): Promise<AxiosResponse<GenericAuthResponse<Profile>>> => {
    return api.get<GenericAuthResponse<Profile>>(PROFILE_DETAILS_URL, getAuthHeader());
  },

  updateName: async (first_name: string, last_name: string): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.put<GenericAuthResponse>(UPDATE_NAME_URL, { first_name, last_name } as UpdateNamePayload, getAuthHeader());
  },

  updatePassword: async (
    current_password: string,
    new_password: string
  ): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.put<GenericAuthResponse>(
      UPDATE_PASSWORD_URL,
      { current_password, new_password } as UpdatePasswordPayload,
      getAuthHeader()
    );
  },

  uploadProfilePicture: async (base64Image: string): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.post<GenericAuthResponse>(
      UPLOAD_PICTURE_URL,
      { base64Image } as UploadPicturePayload,
      getAuthHeader()
    );
  },

  updatePhoneNumber: async (phone_number: string): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.put<GenericAuthResponse>(
      UPDATE_PHONE_URL,
      { phone_number } as UpdatePhonePayload,
      getAuthHeader()
    );
  },

  verifyPhoneNumber: async (otp: string): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.post<GenericAuthResponse>(VERIFY_PHONE_URL, { otp } as VerifyPhonePayload, getAuthHeader());
  },

  setPreferredLanguage: async (language: string): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.put<GenericAuthResponse>(
      UPDATE_LANGUAGE_URL,
      { language } as UpdateLanguagePayload,
      getAuthHeader()
    );
  },

  enableTwoFactorAuth: async (): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.put<GenericAuthResponse>(ENABLE_2FA_URL, {}, getAuthHeader());
  },

  disableTwoFactorAuth: async (): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.put<GenericAuthResponse>(DISABLE_2FA_URL, {}, getAuthHeader());
  },

  deleteAccount: async (): Promise<AxiosResponse<GenericAuthResponse>> => {
    return api.delete<GenericAuthResponse>(DELETE_ACCOUNT_URL, getAuthHeader());
  },
};

const authServices = {
  authService,
  profileService,
};

export default authServices;